import { useState } from 'react';
import { Info } from 'lucide-react';
import type { ChoroplethLegendSpec } from '../../lib/choroplethLegend';
import {
  choroplethGradientCss,
  formatLegendValue,
  legendTitleWithUnit,
  stopLabelPosition,
  stopPercent,
} from '../../lib/choroplethLegend';

/** Matches the reading marker on the scale bar. */
function LegendReadingDot({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'h-3 w-3' : 'h-2.5 w-2.5';
  return (
    <span
      className={`inline-block shrink-0 rounded-full border-2 border-white bg-rose-500 shadow-sm dark:border-gray-900 ${dim}`}
      aria-hidden
    />
  );
}

interface ChoroplethColorScaleProps extends ChoroplethLegendSpec {
  variant?: 'map' | 'panel';
  markerValue?: number | null;
  infoText?: string;
  /** Short map label — distinguishes stacked forecast vs climate legends. */
  mapBadge?: string;
  mapBadgeClassName?: string;
  className?: string;
}

/** Horizontal gradient bar with tick marks — deck.gl-style choropleth legend. */
export function ChoroplethColorScale({
  title,
  unit,
  stops,
  variant = 'map',
  markerValue = null,
  infoText,
  mapBadge,
  mapBadgeClassName = 'text-gray-600 dark:text-gray-300',
  className = '',
}: ChoroplethColorScaleProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const gradient = choroplethGradientCss(stops);
  const isMap = variant === 'map';
  const markerPct =
    markerValue != null && Number.isFinite(markerValue)
      ? Math.min(100, Math.max(0, stopPercent(markerValue, stops)))
      : null;


  if (isMap) {
    const hasMarker = markerPct != null && markerValue != null;
    const markerLabel = hasMarker
      ? `Your reading today: ${formatLegendValue(markerValue!)}${unit ? ` ${unit}` : ''}`
      : null;
    const showTooltip = infoOpen && (infoText || markerLabel);
    const scaleTrackWidth = 'w-[152px]';

    return (
      <div className={`pointer-events-auto relative ${className}`}>
        <div className="rounded-xl border border-gray-200/80 bg-white px-2.5 py-2 shadow-lg dark:border-gray-700/60 dark:bg-gray-900">
          {mapBadge && (
            <span
              className={`mb-1 block text-[9px] font-semibold uppercase tracking-wide ${mapBadgeClassName}`}
            >
              {mapBadge}
            </span>
          )}
          <div className={`shrink-0 ${scaleTrackWidth}`}>
            <div className="flex items-center gap-1.5">
              <div
                className="relative h-[5px] min-w-0 flex-1"
                role="img"
                aria-label={`${legendTitleWithUnit(title, unit)} color scale`}
              >
                <div
                  className="h-[5px] w-full rounded-full border border-black/20 shadow-sm dark:border-white/25"
                  style={{ background: gradient }}
                />
                {stops.map(({ value }) => (
                  <span
                    key={value}
                    className="pointer-events-none absolute top-1/2 h-[5px] w-px -translate-x-1/2 -translate-y-1/2 bg-gray-800/90 dark:bg-white/90"
                    style={{ left: `${stopPercent(value, stops)}%` }}
                    aria-hidden
                  />
                ))}
                {markerPct != null && (
                  <span
                    className="pointer-events-none absolute top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${markerPct}%` }}
                    aria-hidden
                  >
                    <LegendReadingDot size="md" />
                  </span>
                )}
              </div>

              {infoText && (
                <button
                  type="button"
                  onClick={() => setInfoOpen((v) => !v)}
                  aria-expanded={infoOpen}
                  aria-label={`About ${legendTitleWithUnit(title, unit)} legend`}
                  className="relative z-10 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Info size={11} strokeWidth={2.25} />
                </button>
              )}
            </div>

            <div className="relative mt-1 h-[10px] w-full">
              {stops.map(({ value }, index) => {
                const pct = stopPercent(value, stops);
                const pos = stopLabelPosition(index, stops.length, pct);
                return (
                  <span
                    key={`label-${value}`}
                    className="pointer-events-none absolute whitespace-nowrap text-[8px] font-medium tabular-nums leading-none text-gray-800 dark:text-gray-100"
                    style={pos}
                  >
                    {formatLegendValue(value)}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {infoOpen && showTooltip && (
          <div
            className="absolute right-0 top-full z-[100] mt-1.5 max-w-[220px] rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-[10px] leading-snug text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            role="tooltip"
          >
            {infoText ? <p>{infoText}</p> : null}
            {markerLabel ? (
              <p className={`flex items-center gap-1.5 ${infoText ? 'mt-1.5' : ''}`}>
                <LegendReadingDot />
                <span>{markerLabel}</span>
              </p>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`mx-2 mb-2 select-none rounded-lg border border-gray-200/80 bg-gray-50/90 px-2.5 py-2 dark:border-gray-700/60 dark:bg-gray-800/50 ${className}`}
      role="img"
      aria-label={`${legendTitleWithUnit(title, unit)} color scale`}
    >
      <p className="mb-1.5 text-[11px] font-medium leading-none text-gray-700 dark:text-gray-200">
        {legendTitleWithUnit(title, unit)}
      </p>
      <div className="relative">
        <div
          className="h-[7px] w-full rounded-[1px] border border-gray-300/70 dark:border-gray-600/70"
          style={{ background: gradient }}
        />
        {stops.map(({ value }) => (
          <span
            key={value}
            className="pointer-events-none absolute top-0 h-[11px] w-px -translate-x-1/2 bg-gray-700 dark:bg-gray-300"
            style={{ left: `${stopPercent(value, stops)}%` }}
            aria-hidden
          />
        ))}
        {markerPct != null && (
          <span
            className="pointer-events-none absolute top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${markerPct}%` }}
            aria-hidden
          >
            <LegendReadingDot size="md" />
          </span>
        )}
      </div>
      <div className="relative mt-0.5 h-[12px]">
        {stops.map(({ value }, index) => {
          const pct = stopPercent(value, stops);
          const pos = stopLabelPosition(index, stops.length, pct);
          return (
            <span
              key={`label-${value}`}
              className="absolute whitespace-nowrap text-[10px] tabular-nums leading-none text-gray-600 dark:text-gray-400"
              style={pos}
            >
              {formatLegendValue(value)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
